/* Shared by product galleries, customer stories and the Our Approach film. */
(() => {
  'use strict';
  const players = new WeakMap();
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const nearby = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(({target, isIntersecting}) => {
      const player = players.get(target);
      player.visible = isIntersecting;
      if (isIntersecting) player.prepare();
      if (player.background) {
        if (isIntersecting && !document.hidden && !reducedMotion.matches) player.play();
        else target.pause();
      }
    });
  }, {rootMargin: '450px 0px'}) : null;

  function initialize(video) {
    if (players.has(video)) return;
    const background = video.hasAttribute('autoplay');
    const container = video.parentElement;
    container.classList.add('nf-media');
    if (!background) container.classList.add('nf-video');
    container.querySelector('[class*="VideoTile_videoOverlay"]')?.remove();
    container.querySelectorAll('.nf-video-tools,.nf-video-cover,.nf-video-notice').forEach(el => el.remove());
    container.querySelectorAll('button[aria-label="Play"]').forEach(button => button.parentElement.remove());
    video.playsInline = true;
    video.preload = 'metadata';
    video.controls = !background;
    if (background) video.muted = true;
    if (background && reducedMotion.matches) video.pause();

    // Keep an independent cover: mobile browsers may discard the native poster on play().
    const cover = document.createElement('img');
    cover.className = 'nf-video-cover';
    cover.src = video.poster;
    cover.alt = '';
    cover.setAttribute('aria-hidden', 'true');
    cover.style.objectFit = background ? 'cover' : 'contain';
    container.append(cover);
    const notice = document.createElement('div');
    notice.className = 'nf-video-notice';
    notice.hidden = true;
    notice.innerHTML = '<span role="status" aria-live="polite"></span><button type="button">Retry video</button>';
    container.append(notice);
    let requested = false, timer, frameRequest;
    const status = notice.querySelector('span');
    const hideCover = () => {
      if (video.paused || video.readyState < 2) return;
      container.classList.add('nf-video-has-frame');
      notice.hidden = true;
      clearTimeout(timer);
    };
    const showFrame = () => {
      if (video.requestVideoFrameCallback) {
        if (frameRequest !== undefined) video.cancelVideoFrameCallback?.(frameRequest);
        frameRequest = video.requestVideoFrameCallback(hideCover);
      } else requestAnimationFrame(hideCover);
    };
    const message = text => {
      status.textContent = text;
      notice.hidden = false;
    };
    const prepare = () => {
      if (video.preload === 'auto') return;
      video.preload = 'auto';
      // Changing preload can be ignored after a browser suspended metadata loading.
      if (video.readyState < 2 && video.paused) video.load();
    };
    const play = () => {
      requested = true;
      prepare();
      notice.hidden = true;
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (requested && video.readyState < 2) message('Still loading. You can retry the video.');
      }, 10000);
      const attempt = video.play();
      attempt?.catch(error => {
        clearTimeout(timer);
        if (error.name === 'AbortError') return;
        requested = false;
        container.classList.remove('nf-video-has-frame');
        message(error.name === 'NotAllowedError' ? 'Tap to play this video.' : 'Video could not load. Please retry.');
      });
    };
    notice.querySelector('button').addEventListener('click', () => {
      video.load();
      play();
    });
    video.addEventListener('nf:video-play', play);
    video.addEventListener('playing', showFrame);
    video.addEventListener('loadeddata', () => { if (!video.paused) showFrame(); });
    video.addEventListener('pause', () => { requested = false; clearTimeout(timer); });
    video.addEventListener('error', () => {
      clearTimeout(timer);
      container.classList.remove('nf-video-has-frame');
      message('Video could not load. Please retry.');
    });
    const size = () => {
      if (!video.videoWidth) return;
      container.style.setProperty('--video-native-width', `${video.videoWidth}px`);
      container.style.setProperty('--video-ratio', `${video.videoWidth} / ${video.videoHeight}`);
    };
    video.addEventListener('loadedmetadata', size);
    size();

    if (!background) {
      const tools = document.createElement('div');
      tools.className = 'nf-video-tools';
      tools.innerHTML = '<button type="button" data-video-play aria-label="Play video">▶</button><button type="button" data-video-sound aria-label="Unmute video">Sound on</button><button type="button" data-video-fullscreen aria-label="Enter fullscreen">⛶</button>';
      container.append(tools);
      const toggle = tools.querySelector('[data-video-play]');
      const sound = tools.querySelector('[data-video-sound]');
      const fullscreen = tools.querySelector('[data-video-fullscreen]');
      const sync = () => {
        toggle.textContent = video.paused ? '▶' : 'Ⅱ';
        toggle.setAttribute('aria-label', video.paused ? 'Play video' : 'Pause video');
        sound.textContent = video.muted ? 'Sound on' : 'Mute';
        sound.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
      };
      toggle.addEventListener('click', () => video.paused ? play() : video.pause());
      sound.addEventListener('click', () => { video.muted = !video.muted; sync(); });
      ['play', 'pause', 'ended', 'volumechange'].forEach(event => video.addEventListener(event, sync));
      fullscreen.addEventListener('click', async () => {
        try {
          if (document.fullscreenElement) await document.exitFullscreen();
          else if (video.webkitEnterFullscreen) video.webkitEnterFullscreen();
          else if (container.requestFullscreen) await container.requestFullscreen();
        } catch { message('Use the video player’s fullscreen control to expand this video.'); }
      });
      document.addEventListener('fullscreenchange', () => fullscreen.setAttribute('aria-label', document.fullscreenElement === container ? 'Exit fullscreen' : 'Enter fullscreen'));
      video.addEventListener('play', () => document.querySelectorAll('video').forEach(other => {
        if (other !== video && !other.paused) other.pause();
      }));
      sync();
    }
    players.set(video, {background, prepare, play, visible: false});
    if (nearby) nearby.observe(video);
    else prepare();
    if (!video.paused) showFrame();
  }
  const scan = root => {
    if (root.matches?.('video')) initialize(root);
    root.querySelectorAll?.('video').forEach(initialize);
  };
  scan(document);
  // Theme editor sections and responsive gallery clones must get the same behavior.
  new MutationObserver(records => records.forEach(record => record.addedNodes.forEach(scan)))
    .observe(document.body, {childList: true, subtree: true});
  const resumeBackgrounds = () => {
    if (document.hidden || reducedMotion.matches || document.querySelector('dialog[open]')) return;
    document.querySelectorAll('video').forEach(video => {
      const player = players.get(video);
      if (player?.background && player.visible) player.play();
    });
  };
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) document.querySelectorAll('video').forEach(video => video.pause());
    else resumeBackgrounds();
  });
  document.addEventListener('close', resumeBackgrounds, true);
})();
